<template>
  <BaseTemplate>
    <template slot="header-content">
      <div class="col-12 description-section">
        <div class="description mb-4">
          This is the OCR demo for the paper “Transformer-based Multilingual Optical Character Recognition System for Thai and English Documents”.
        </div>
        <div class="description mb-4">
          Select model and downstream tasks. You can select the recognition model (documents or handwritten models) for comparison. Then, select example images or upload images for OCR prediction.
        </div>
      </div>
      <div class="col-12 description-section">
        <div class="row">
          <div class="col-12 col-md-3 mb-4">
            <span class="model-select-title">Select model</span>
          </div>
          <div class="col-12 col-md-9 mb-4">
            <a class="btn btn-outline-primary model-select-btn active me-4" role="button">CRNN</a>
            <a class="btn btn-outline-primary model-select-btn me-4" role="button">TrOCR</a>
          </div>
          <div class="col-12 col-md-3 mb-4">
            <span class="model-select-title">Select downstream task</span>
          </div>
          <div class="col-12 col-md-9 mb-4">
            <a class="btn btn-outline-primary model-select-btn active me-4" role="button">Printed document</a>
            <a class="btn btn-outline-primary model-select-btn me-4" role="button">Handwritten document</a>
          </div>
        </div>
      </div>
    </template>
    <template slot="input-content">
      <ImageSelect 
        :listImages="demo_datas" 
        v-model="data_select" 
        class="mb-3"
        :results="results"
        :type="page_type"
        :upload_image="upload_image"
      />
      
      <div class="d-grid gap-2 d-md-flex">
        <input accept="image/*" type="file" ref="file" style="display: none" v-on:change="clickUpload()">
        <ButtonComponent class="demo-bg-primary" icon="photo_library" text="Upload" @click.native="$refs.file.click()"/>
        <ButtonComponent 
          class="demo-bg-danger w-100" icon="east" text="Run Extraction" 
          :class="{'disabled': !data_select}"
          @click.native="clickSubmit()"/>
      </div>
    </template>
    <template slot="output-content">
      <BaseResult :is_loading="is_loading" style="height: 653px;">
        <template slot="result-title">
          Result
        </template>
        <template slot="result-detail">
          <div class="row mb-3 align-items-center" v-for="(car,index) in results" :key="index">
            <div class="col-4">
              <canvas :ref="'canvas-'+index" class="img-fluid border bg-secondary" :style="{ 'border': '3px solid ' + car.color+ '!important' }"></canvas>
            </div>
            <div class="col-4 d-flex flex-column">
                <span class="fw-bold">ทะเบียนรถ</span>
                <span>{{ textIfNUll(car.license) }}</span>
              </div>
            <div class="col-4 d-flex flex-column">
              <span class="fw-bold">จังหวัด</span>
              <span>{{ textIfNUll(car.province) }}</span>
            </div>
          </div>
        </template>        
      </BaseResult>
    </template>
    <template slot="footer-content">
      <div class="col-12 description-section mt-5">
        <div class="title mb-4">
          <span class="h1 fw-bold">Model Performance and Error Analysis</span>
        </div>
        <div class="description mb-4">
          Below we provide the model performance and example predictions of the best models<br>
          (Transformer-based model).
        </div>
        <div class="title mb-4">
          <span class="h2 fw-bold">Model performance</span>
        </div>
        <div class="description mb-4">
          We evaluate the performance of the models on different document types. You can explore more in the publication if you would like to learn more about how we evaluate the model.  
        </div>
        <div class="description mb-4 table-responsive" style="border-radius: 16px;overflow: hidden;">
          <table class="table table-bordered rounded-3 m-0 model-select-table">
            <thead>
              <tr>
                <th rowspan="2">Models</th>
                <th colspan="4">CER for each document type</th>
              </tr>
              <tr>
                <th>Scan</th>
                <th>Camera</th>
                <th>Web documents</th>
                <th>Handwritten</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>CNN-<br>RNN (CRNN)</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td>TrOCR-<br>small-<br>stage 1</td>
                <td>0.73</td>
                <td>1.32</td>
                <td></td>
                <td>3.14</td>
              </tr>
            </tbody>
            
          </table>  
        </div>
        <div class="title mb-4">
          <span class="h2 fw-bold">Model predictions and error analysis</span>
        </div>
        <div class="description mb-4">
          In the following table, we provide example predictions of text line recognition and its corresponding character error rate (CER) by sorting the prediction from high to low CER.
        </div>
        <div class="card mb-4" style="border-radius: 20px;overflow: hidden;">
          <div class="card-body p-0">
            <nav class="nav flex-row model-select-tab-group">
              <a class="flex-fill text-center nav-link active" role="button">Scanned</a>
              <a class="flex-fill text-center nav-link" role="button">Photographed</a>
              <a class="flex-fill text-center nav-link" role="button">Website</a>
              <a class="flex-fill text-center nav-link" role="button">Receipts</a>
              <a class="flex-fill text-center nav-link" role="button">Handwritten</a>
            </nav>
            <div class="col-12">
              <div class="table-responsive">
                <table class="table table-borderless model-select-prediction-table">
                  <thead>
                    <tr>
                      <td>Textline images</td>
                      <td>Annotated text</td>
                      <td>Predicted text</td>
                      <td>CER</td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td></td>
                      <td>***</td>
                      <td>"หจก.โรงแรมบางรักแมนชั่น"</td>
                      <td>2300.0</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              .... contents
            </div>
          </div>
        </div>
        <div class="description mb-4">
          Character Error Rate (CER) is defined as
        </div>
        <div class="title mb-4">
          <span class="h2 fw-bold">CER = 100 * (s + d + i) / (s + d + c).</span>
        </div>
        <div class="description mb-4">
          where s, d, i, and c are substitution, deletions, insertion, and correct characters respectively. We utilize fastwer library for calculating CER.  
        </div>
      </div>
    </template>
  </BaseTemplate>
</template>

<script>
import BaseTemplate from '@/templates/BaseTemplate.vue';
import ImageSelect from '@/components/select/ImageSelect.vue';
import ButtonComponent from '@/components/button/ButtonComponent.vue';
import BaseResult from '@/components/result/BaseResult.vue';
import axios from "axios"

import { _data } from "@/store/_data_lpr.ts";
export default {
  components: { 
    BaseTemplate,
    ImageSelect, 
    BaseResult,
    ButtonComponent
  },
  name: 'DemoLprHome',
  data() {
    return {
      page_type: "upload",
      demo_datas: _data.map(x=>{ x.imagePath = require('@/'+x.file_path); x.is_select = false; return x; }),
      data_select: null,
      is_loading: false,
      results: [],
      upload_image: "",
      colors: ["ff0000","ff8700","ffd300","deff0a","a1ff0a","0aff99","0aefff","147df5","580aff","be0aff"]
    }
  },
  updated: function () {
    const self = this
    this.$nextTick(function () {
      // Code that will run only after the
      // entire view has been re-rendered
      self.results.map((x,i)=>{
        const canvas = self.$refs["canvas-" + i]
        const context = canvas[0].getContext('2d')
        let img = new Image()
        img.src = self.upload_image
        // Loading Pictures
        img.onload = function(){
          if(img.complete){
              var hRatio = context.canvas.width  / (x.xmax-x.xmin);
              var vRatio =  context.canvas.height / (x.ymax-x.ymin);
              var ratio  = Math.min ( hRatio, vRatio );
              var centerShift_x = ( context.canvas.width - (x.xmax-x.xmin)*ratio ) / 2;
              var centerShift_y = ( context.canvas.height - (x.ymax-x.ymin)*ratio ) / 2;  
              context.clearRect(0,0,context.canvas.width, context.canvas.height);

              context.drawImage(img, 
                x.xmin,x.ymin, 
                (x.xmax-x.xmin), (x.ymax-x.ymin),
                centerShift_x,centerShift_y,
                (x.xmax-x.xmin)*ratio, (x.ymax-x.ymin)*ratio
              );
          }
        }
      })
    })
  },
  watch: {
    data_select: function(){
      this.results = [],
      this.upload_image = ""
    },
  },
  methods:{
    clickSubmit(){
      if(this.data_select !== null) {
        if(this.page_type === 'demo'){
          this.is_loading = true;
          const self = this;
          setTimeout(function(){
            self.is_loading = false;
            self.results = self.data_select.data.predictions
            self.results.map((x,i)=>{
              x.color = "#" + (self.colors[i] ?? self.colors[0])
            })
            // string image demo
            self.upload_image = self.data_select.data.imagePath
          }, 1200);
        }
        if(this.page_type === 'upload'){
          this.is_loading = true;

          let request = new XMLHttpRequest();
          const self = this;
          request.responseType = "blob";
          request.onload = function() {
            let formData = new FormData();
            formData.append("image_input", request.response);

            self.getResult(formData);
          }
          request.open("GET", self.data_select.data.imagePath);
          request.send();
        }
      }
    },
    clickUpload(){
      this.data_select = {
        data: {
          imagePath: URL.createObjectURL(this.$refs.file.files[0])
        },
        type: "upload"
      }
    },
    getResult(formData){
      const self = this;
      let config = {
        method: 'post',
        url: 'https://ourworks-api-lpr.loolootest.com/uploadfile/',
        headers: { 
          'Content-Type': 'multipart/form-data',
        },
        data : formData
      };
      axios(config)
        .then(function(res){
          self.results = res.data.predictions
          self.results.map((x,i)=>{
            x.color = "#" + (self.colors[i] ?? self.colors[0])
          })
          self.upload_image = self.data_select.data.imagePath
        })
        .catch(function(err){
          console.log(err);
        })
        .finally(()=>{
          self.is_loading = false;
        });
    },
    textIfNUll(item) {
      return !item? '-' : item;
    },
  },
}
</script>

<style scoped>
  .model-select-title::v-deep {
    font-weight: 700;
    font-size: 20px;
    line-height: 150%;
    /* or 30px */

    letter-spacing: 0.0168em;
    font-feature-settings: 'tnum' on, 'lnum' on;

    /* White */

    color: #FFFFFF;
  }

  .model-select-btn::v-deep {
    padding: 16px 86px;
    gap: 10px;

    color: #FFFFFF;
    font-style: normal;
    font-weight: 700;
    font-size: 20px;
    line-height: 150%;

    border: 1px solid #FFFFFF;
    border-radius: 16px;
    border-width: 1px;
  }

  .btn-outline-primary::v-deep:hover {
    background-color: #FB7A1E;
    border-color: #FB7A1E;
  }

  .btn-outline-primary::v-deep.active {
    background-color: #FB7A1E;
    border-color: #FB7A1E;
  }

  .model-select-table::v-deep {
    border: 0px;
  }

  .model-select-table::v-deep {
    background-color: #FFFFFF;
  }
  
  .model-select-table::v-deep thead tr th {
    font-weight: 700;
    font-size: 24px;
    line-height: 135%;
    vertical-align: middle;
    text-align: center;
    font-feature-settings: 'tnum' on, 'lnum' on;

    /* Black */
    color: #000000;
  }

  .model-select-table::v-deep thead tr:not(:first-child) th {
    font-size: 20px;
    line-height: 135%;
    vertical-align: middle;
    text-align: center;
    font-feature-settings: 'tnum' on, 'lnum' on;

    /* Black */
    color: #000000;
  }

  .model-select-table::v-deep tbody tr td:first-child {
    padding-left: 24px;
  }

  .model-select-table::v-deep tbody tr td:not(:first-child) {
    font-weight: 300;
    font-size: 20px;
    line-height: 160%;
    /* or 32px */

    vertical-align: middle;
    text-align: center;
    letter-spacing: 0.0168em;
    font-feature-settings: 'tnum' on, 'lnum' on;

    /* Black */
    color: #000000;
  }

  .model-select-tab-group::v-deep a {
    font-weight: 700;
    font-size: 24px;
    line-height: 135%;
    color: #616161;

    padding-top: 20px;
    padding-bottom: 20px;
  }

  .model-select-tab-group::v-deep a.active {
    color: #000000;
    border-bottom: 8px solid #F8C784;
  }

  .model-select-prediction-table::v-deep thead tr td {
    font-weight: 300;
    font-size: 20px;
    line-height: 200%;
    text-align: center;
    vertical-align: middle;
    letter-spacing: 0.0168em;
    font-feature-settings: 'tnum' on, 'lnum' on;

    color: #000000;

    background-color: #f2f2f2;

  }
</style>
